/**
 * InspectionTimeline - Linha do tempo visual da vistoria
 *
 * Padrão visual herdado da timeline do modal de proposta (ProposalTimeline):
 * badge circular colorido com ícone por tipo de evento, conector vertical,
 * agrupamento por dia ("Hoje" / "Ontem" / data), destaque no evento mais
 * recente e motivo em linha própria. Adaptada aos eventos da vistoria
 * (checklist_events) sem alterar a RPC `get_checklist_timeline`.
 *
 * @version 1.2.0
 * RELEASE: 2026-09-28 v1.2.0 - Exibe troca de tipo/responsável com dados anteriores e novos.
 * RELEASE: 2026-09-27 v1.1.0 - Adiciona variante compacta para a coluna
 *          lateral permanente do modal, sem cartões em volta dos eventos.
 * RELEASE: 2026-09-26 v1.0.0 - Criação: substitui a lista simples de bolinhas
 *          pretas da aba "Linha do tempo" do modal de vistoria.
 */
import React from 'react';
import {
  FilePlus2, PlayCircle, PenLine, Send, CheckCircle2, XCircle,
  RefreshCw, Ban, RotateCcw, ShieldAlert, History, UserRoundCog, type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MapLinkButton } from './MapLinkButton';

/** Evento retornado pela RPC get_checklist_timeline. */
export interface InspectionTimelineEvent {
  id: string;
  event_type: string;
  actor_full_name: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
}

/** Ícone, cores e rótulo em pt-BR por tipo de evento da vistoria. */
const EVENT_MAP: Record<string, { icon: LucideIcon; iconClass: string; badgeClass: string; label: string }> = {
  inspection_created: { icon: FilePlus2, iconClass: 'text-muted-foreground', badgeClass: 'bg-muted', label: 'Vistoria criada' },
  inspection_started: { icon: PlayCircle, iconClass: 'text-blue-600 dark:text-blue-400', badgeClass: 'bg-blue-500/10', label: 'Vistoria iniciada' },
  item_answered: { icon: PenLine, iconClass: 'text-indigo-600 dark:text-indigo-400', badgeClass: 'bg-indigo-500/10', label: 'Item respondido' },
  item_changed: { icon: PenLine, iconClass: 'text-indigo-600 dark:text-indigo-400', badgeClass: 'bg-indigo-500/10', label: 'Item alterado' },
  item_corrected: { icon: PenLine, iconClass: 'text-indigo-600 dark:text-indigo-400', badgeClass: 'bg-indigo-500/10', label: 'Item corrigido' },
  inspection_submitted: { icon: Send, iconClass: 'text-amber-600 dark:text-amber-400', badgeClass: 'bg-amber-500/10', label: 'Enviada para revisão' },
  item_approved: { icon: CheckCircle2, iconClass: 'text-emerald-600 dark:text-emerald-400', badgeClass: 'bg-emerald-500/10', label: 'Item aprovado' },
  item_rejected: { icon: XCircle, iconClass: 'text-destructive', badgeClass: 'bg-destructive/10', label: 'Item reprovado' },
  correction_requested: { icon: RefreshCw, iconClass: 'text-orange-600 dark:text-orange-400', badgeClass: 'bg-orange-500/10', label: 'Correção solicitada' },
  inspection_approved: { icon: CheckCircle2, iconClass: 'text-emerald-600 dark:text-emerald-400', badgeClass: 'bg-emerald-500/10', label: 'Vistoria aprovada' },
  inspection_rejected: { icon: XCircle, iconClass: 'text-destructive', badgeClass: 'bg-destructive/10', label: 'Vistoria reprovada' },
  inspection_cancelled: { icon: Ban, iconClass: 'text-muted-foreground', badgeClass: 'bg-muted', label: 'Vistoria cancelada' },
  inspection_reopened: { icon: RotateCcw, iconClass: 'text-amber-600 dark:text-amber-400', badgeClass: 'bg-amber-500/10', label: 'Vistoria reaberta' },
  fraud_alert: { icon: ShieldAlert, iconClass: 'text-destructive', badgeClass: 'bg-destructive/10', label: 'Alerta de fraude' },
  assignee_changed: { icon: UserRoundCog, iconClass: 'text-primary', badgeClass: 'bg-primary/10', label: 'Responsável alterado' },
};

const ASSIGNEE_LABEL: Record<string, string> = { user: 'Usuário interno', queue: 'Fila global', owner: 'Proprietário', provider: 'Prestador' };

/** Resume a alteração sem expor identificadores internos completos. */
function assigneeChangeText(event: InspectionTimelineEvent): string | null {
  if (event.event_type !== 'assignee_changed') return null;
  const before = event.metadata?.before ?? {};
  const after = event.metadata?.after ?? {};
  return `${ASSIGNEE_LABEL[String(before.mode)] ?? 'Não definido'} → ${ASSIGNEE_LABEL[String(after.mode)] ?? 'Não definido'}`;
}

/** Formata apenas o horário (a data fica no separador do dia). */
function formatTime(value: string): string {
  return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** Rótulo do dia (Hoje / Ontem / dd de mês de aaaa). */
function formatDayLabel(value: string): string {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(date, today)) return 'Hoje';
  if (sameDay(date, yesterday)) return 'Ontem';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

/**
 * Linha do tempo da vistoria: eventos agrupados por dia, do mais recente
 * ao mais antigo (a RPC já ordena; o agrupamento preserva essa ordem).
 */
export function InspectionTimeline({ events, variant = 'default' }: {
  events: InspectionTimelineEvent[];
  variant?: 'default' | 'compact';
}) {
  const compact = variant === 'compact';
  // Agrupa por dia preservando a ordem (mais recente primeiro)
  const groups = React.useMemo(() => {
    const result: Array<{ label: string; items: InspectionTimelineEvent[] }> = [];
    (events ?? []).forEach((event) => {
      const label = formatDayLabel(event.created_at);
      const last = result[result.length - 1];
      if (last && last.label === label) last.items.push(event);
      else result.push({ label, items: [event] });
    });
    return result;
  }, [events]);

  if (!events || events.length === 0) {
    return (
      <div className={cn('border border-dashed border-border py-8 text-center', compact ? 'rounded-md' : 'rounded-lg')}>
        <History className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
        <p className="text-xs text-muted-foreground">Nenhum evento registrado.</p>
      </div>
    );
  }

  return (
    <div className={cn(compact ? 'space-y-5' : 'space-y-4')}>
      {groups.map((group) => (
        <div key={group.label}>
          {/* Separador de dia */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="relative">
            {/* Conector vertical alinhado ao centro dos badges */}
            <div className="absolute top-3 bottom-3 left-[15px] w-px bg-border" aria-hidden />

            <div className={cn(compact ? 'space-y-3' : 'space-y-2.5')}>
              {group.items.map((event, index) => {
                const meta = EVENT_MAP[event.event_type] ?? {
                  icon: History, iconClass: 'text-muted-foreground', badgeClass: 'bg-muted', label: event.event_type,
                };
                const Icon = meta.icon;
                const isLatest = groups[0] === group && index === 0;
                const reason = typeof event.metadata?.reason === 'string' && event.metadata.reason.trim()
                  ? event.metadata.reason.trim() : null;
                const geo = event.metadata?.geo;
                const hasGeo = geo?.lat != null && geo?.lng != null;
                const assigneeChange = assigneeChangeText(event);

                return (
                  <div key={event.id} className="relative flex gap-3">
                    {/* Badge circular do ícone */}
                    <div className={cn(
                      'relative z-[1] shrink-0 rounded-full flex items-center justify-center',
                      compact ? 'h-7 w-7' : 'h-8 w-8',
                      meta.badgeClass,
                      isLatest && 'ring-2 ring-primary/30 ring-offset-2 ring-offset-background',
                    )}>
                      <Icon className={cn(compact ? 'h-3.5 w-3.5' : 'h-4 w-4', meta.iconClass)} />
                      {/* Marcador de novidade no evento mais recente */}
                      {isLatest && <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary" />}
                    </div>

                    {/* Cartão do evento */}
                    <div className={cn(
                      'flex-1 min-w-0',
                      compact ? 'py-0.5' : 'rounded-lg border border-border/60 bg-card px-3 py-2',
                    )}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-medium text-foreground min-w-0 break-words">
                          {meta.label}
                          {event.metadata?.name ? ` — ${event.metadata.name}` : ''}
                        </p>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                          {formatTime(event.created_at)}
                        </span>
                      </div>

                      {/* Ator + geolocalização (quando registrada no evento) */}
                      <p className="text-[11px] text-muted-foreground mt-0.5 break-words">
                        {event.actor_full_name ?? 'Sistema'}
                        {hasGeo && (
                          <>
                            {` · ${Number(geo.lat).toFixed(5)}, ${Number(geo.lng).toFixed(5)}`}
                            <span className="ml-1 inline-flex align-middle">
                              <MapLinkButton lat={Number(geo.lat)} lng={Number(geo.lng)} />
                            </span>
                          </>
                        )}
                      </p>

                      {/* Motivo registrado (reprovação, cancelamento, reabertura) */}
                      {reason && (
                        <p className="mt-1 break-words text-foreground/90 line-clamp-3 text-[11px]" title={reason}>
                          <span className="font-medium text-muted-foreground">Motivo: </span>
                          {reason}
                        </p>
                      )}
                      {assigneeChange && <p className="mt-1 text-[11px] text-foreground/90">{assigneeChange}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
